import { DataStore, ItemReference, DataModel, Query, QueryResult } from 'src/core/stores/dataStore'
import { FormStore, FormModel } from 'src/core/stores/formStore'
import { repository, AsyncAction, reduce } from 'redux-scaffolding-ts'
import { container } from 'src/inversify.config'
import { Validator } from "lakmus"
import { CommandResult } from '../core/stores/types'
import { AxiosResponse } from 'axios'
import HttpService from '../core/services/http.service'
import { createPatch } from 'rfc6902'
import autobind from 'autobind-decorator'
import { delay } from '../core/utils/object'

export interface ProjectSummary {
  id: string
  title: string
  isPrivate: boolean
  rdTaxRelief: boolean
  tiTaxRelief: boolean
  reference: string
  fromDate: number
  toDate: number
  managers: string[]
  members: string[]
  tasks: [number, string, boolean, boolean][]
  allowedCir: boolean
  allowedCii: boolean
  approved: boolean
  createdOn: Date
  createdBy: string
  billable: boolean
  code: string
}

export interface ProjectItem extends NewProjectItem {
  id: number
  approved: boolean
  createdOn: Date
  createdBy: string
}

export interface NewProjectItem {
  title: string
  ignoreProjectCreation?: boolean
  reference: string
  isPrivate: boolean
  rdTaxRelief: boolean
  tiTaxRelief: boolean
  firstTaskTitle: string
  firstTaskReference: string
  fromDate: number
  toDate?: number
  allowedCir: boolean
  allowedCii: boolean
  code: string
  billable: boolean
}

@repository("@@PROJECTS", "projects.summary")
export class ProjectsStore extends DataStore<ProjectSummary> {
  baseUrl: string = `${process.env.PUBLIC_URL}`;

  constructor() {
    super('PROJECT', {
      count: 0,
      isBusy: false,
      items: [],
      result: undefined,
      discard: item => { }
    }, container)

    //Add custom reducer to fix sticky error message on update entity
    this.addReducer('PROJECT_UPDATED', (): any => {
      return {
        onStart: () => ({ ...this.state, isBusy: true }),
        onSuccess: (result: any, item: any) => {
          return {
            ...this.state,
            items: this.state.items.map(o => {
              if ((o.item as any)[this.rowKey] == ((item as any)[this.rowKey] || result.data.identifier || (this.useTitleKey ? result.data.title : result.data.aggregateRootId || result.data.correlationId))) {
                o.state = 'Unchanged'
                o.item = Object.assign(o.item, item);
                (o.item as any)[this.rowKey] = result.data.identifier || (o.item as any)[this.rowKey]
              }
              return o
            }),
            isBusy: false,
          }
        },
        onError: (error: any) => ({
          ...this.state, isBusy: false
        })
      }
    }, 'AsyncAction')
  }

  public async getAllAsync(query: Query, data?: any, companyId?: string): Promise<QueryResult<ProjectSummary>> {
    let httpService = this.container.get<HttpService>(HttpService)
    const result = await this.dispatchAsync(this.ENTITY_LIST_UPDATE as string, httpService.get<QueryResult<ProjectSummary>>(`${this.baseUrl}?${DataStore.buildUrl(query)}`, data))

    return result.data
  }

  public async getProjectEmployeeAsync(query: Query, employeeId?: string): Promise<any[]> {
    let httpService = this.container.get<HttpService>(HttpService)
    const result = await this.dispatchAsync(this.ENTITY_LIST_UPDATE as string, httpService.get<any[]>(`${this.baseUrl}/projectsForMember/${employeeId}?${DataStore.buildUrl(query)}`, {}))

    return result.data
  }

  public changeUrl(companyId: string) {
    this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/projects`
  }

  public async setEligibility(projectId: string, fieldName: string, value: boolean) {
    var item = this.state.items.firstOrDefault(x => x.item.id == projectId)
    var httpService = this.container.get<HttpService>(HttpService)
    if (item) {
      var partial = Object.assign(Object.assign({}, item.item), { [fieldName]: value })
      return this.dispatchAsync('PROJECT_UPDATED', httpService.patch(`${this.baseUrl}/${encodeURIComponent(projectId)}`, createPatch(item.item, partial)), partial)
    }
  }


  public async delete(id: string) {
    var httpService = this.container.get<HttpService>(HttpService)
    const result = await httpService.delete(`${this.baseUrl}/${id}`)
    return result.data
  }

  public async exportProjects(query: Query, projectsIds: string[]) {
    let httpService = this.container.get<HttpService>(HttpService)
    const result = await httpService.post(`${this.baseUrl}/export_projects?${DataStore.buildUrl(query)}`, projectsIds)
    return result.data
  }

}

@repository("@@PROJECTS", "projects.report")
export class ReportProjectsStore extends DataStore<ProjectSummary> {
  baseUrl: string = `${process.env.PUBLIC_URL}`;

  constructor() {
    super('PROJECT', {
      count: 0,
      isBusy: false,
      items: [],
      result: undefined,
      discard: item => { }
    }, container)
  }

  public async getAllAsync(query: Query, data?: any, companyId?: string): Promise<QueryResult<ProjectSummary>> {
    let httpService = this.container.get<HttpService>(HttpService)
    const result = await this.dispatchAsync(this.ENTITY_LIST_UPDATE as string, httpService.get<QueryResult<ProjectSummary>>(`${this.baseUrl}?${DataStore.buildUrl(query)}`, data))
    return result.data
  }

  public changeUrl(companyId: string) {
    this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/projects`
  }
}

@repository("@@PROJECTS", "projects.timesheet")
export class TimesheetProjectsStore extends DataStore<ProjectSummary> {
  baseUrl: string = `${process.env.PUBLIC_URL}`;

  constructor() {
    super('PROJECT', {
      count: 0,
      isBusy: false,
      items: [],
      result: undefined,
      discard: item => { }
    }, container)
  }

  public changeUrlByCompany(companyId: string) {
    this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/projects/fromNames`
  }

  public async getAllAsync(query: Query, data?: any): Promise<QueryResult<ProjectSummary>> {
    let httpService = this.container.get<HttpService>(HttpService)
    const result = await this.dispatchAsync(this.ENTITY_LIST_UPDATE as string, httpService.get<QueryResult<ProjectSummary>>(`${this.baseUrl}?${DataStore.buildUrl(query)}`, data))
    return result.data
  }

  public changeUrl(companyId: string) {
    this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/projects`
  }
}

export class NewProjectValidator extends Validator<NewProjectItem> {
  constructor() {
    super()

    this.ruleFor(x => x.title)
      .notNull()
      .withMessage("Title required")
  }
}

export class ProjectValidator extends Validator<ProjectItem> {
  constructor() {
    super()

    this.ruleFor(x => x.title)
      .notNull()
      .withMessage("Title required")
  }
}

const PROJECT_UPDATE_ITEM = "PROJECT_UPDATE_ITEM"
@repository("@@PROJECTS", "projects.detail")
export class ProjectStore extends FormStore<ProjectItem> {
  baseUrl: string = `${process.env.PUBLIC_URL}`;

  protected validate(item: ProjectItem) {
    return new ProjectValidator().validate(item)
  }

  constructor() {
    super('PROJECT', {
      isBusy: false,
      status: 'New',
      item: undefined,
      result: undefined
    }, container)
    this.addReducer(PROJECT_UPDATE_ITEM, (): any => {
      return super.onPatch()
    }, 'AsyncAction')
  }

  public async getById(id: string, params?: any, companyId?: string): Promise<ProjectItem> {
    var httpService = this.container.get<HttpService>(HttpService)
    const result = await this.dispatchAsync(this.ENTITY_LOAD, httpService.get<ProjectItem>(`${this.baseUrl}/${id}`, params))
    return result.data
  }

  public async update(item: ProjectItem, companyId: string) {
    var result = await super.patch(PROJECT_UPDATE_ITEM, `${item.id}`, item) as any
    return result.data as CommandResult<ProjectItem>
  }

  public changeUrl(companyId: string) {
    this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/projects`
  }
}

@repository("@@PROJECTS", "projects.new")
export class NewProjectStore extends FormStore<NewProjectItem> {
  baseUrl: string = `${process.env.PUBLIC_URL}`;
  isTaskError: boolean

  protected validate(item: NewProjectItem) {
    return (new NewProjectValidator()).validate(item)
  }

  constructor() {
    super('NEW_PROJECT', {
      isBusy: false,
      status: 'New',
      item: undefined,
      result: undefined
    }, container)

    this.isTaskError = false
  }

  public changeUrl(companyId: string) {
    this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/companies/${companyId}/projects`
  }

  public postSendEmailManager(titleTask: string, titleProject: string) {
    let httpService = this.container.get<HttpService>(HttpService)
    return httpService.post(`${this.baseUrl}/${titleTask}/${titleProject}/project-exists`, this.state.item)
  }

  @autobind
  public async post(): Promise<CommandResult<NewProjectItem>> {
    const validationResult = this.validate(this.state.item as NewProjectItem)
    if (!validationResult || !validationResult.isValid) {
      this.dispatch(this.ENTITY_VALIDATED, validationResult)
      return Promise.resolve({
        isSuccess: false,
      } as CommandResult<NewProjectItem>)
    } else {
      var httpService = this.container.get<HttpService>(HttpService)
      var item = this.state.item

      return await this.dispatchAsync(this.ENTITY_SAVE, new Promise<CommandResult<NewProjectItem>>((resolve, reject) => {
        httpService.post(`${this.baseUrl}`, item)
          .then(async result => {
            let resultData = (result.data as any)
            if (resultData.isSuccess && resultData.messages.length > 0 && resultData.messages[0].level == "Error") {
              this.isTaskError = true
              reject(resultData.messages[0].body)
            } else {
              resolve(result.data as any)
            }
          })
          .catch(error => {
            this.isTaskError = false
            reject(error)
          })
      }))
    }
  }

  public async update(item: ProjectItem) {
    var httpService = this.container.get<HttpService>(HttpService)
    const result = await httpService.put(`${this.baseUrl}/${item.id}`, item)

    return result.data
  }
}